/**
 * PublicPortalLandingPage — `/c/<slug>` (no auth required)
 *
 * This is the firm's branded client-portal landing page. Fetches branding
 * (logo, color, tagline) and shows two CTAs:
 *   - "I'm a current client — sign in"  → /login?next=/portal
 *   - "Start working with us"  → /c/<slug>/start (signup form)
 *
 * If the firm hasn't enabled public signup, the signup CTA flips to "Contact
 * us" with the firm's contact info.
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, Lock, UserPlus, Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicPortalLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/portal/${slug}`);
        if (r.status === 404) { setNotFound(true); return; }
        if (!r.ok) throw new Error('failed');
        setData(await r.json());
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-pulse w-32 h-8 bg-zinc-200 rounded" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
        <div className="max-w-md text-center">
          <h1 className="text-3xl font-bold mb-3">Portal not found</h1>
          <p className="text-zinc-600 mb-6">
            We couldn't find a client portal at this address. Double-check the link with the firm that invited you.
          </p>
          <Link to="/" className="text-emerald-600 hover:underline">Go home</Link>
        </div>
      </div>
    );
  }

  const brand = data.brand_color || '#10b981';

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-zinc-50" data-testid="public-portal-landing">
      {/* Branded header */}
      <header className="border-b" style={{ borderColor: `${brand}22` }}>
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {data.logo_url ? (
              <img src={data.logo_url} alt={data.name} className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                style={{ background: brand }}
              >
                {(data.name || '?').charAt(0)}
              </div>
            )}
            <div>
              <p className="font-semibold text-lg leading-none">{data.name}</p>
              <p className="text-xs text-zinc-500 mt-1">Client Portal</p>
            </div>
          </div>
          <Link
            to={`/c/${slug}/login`}
            className="text-sm font-medium hover:underline"
            style={{ color: brand }}
            data-testid="portal-landing-signin"
          >
            Sign in
          </Link>
        </div>
      </header>

      {/* Hero */}
      <main className="max-w-5xl mx-auto px-6 py-16 sm:py-24">
        <div className="max-w-2xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-zinc-900 leading-tight tracking-tight">
            {data.tagline || `Welcome to ${data.name}.`}
          </h1>
          <p className="mt-5 text-lg text-zinc-600 leading-relaxed max-w-xl">
            {data.welcome_message || `${data.name} uses this secure portal to share invoices, collect documents, and stay in touch with their clients.`}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            {data.public_signup_enabled && (
              <Button
                onClick={() => navigate(`/c/${slug}/start`)}
                className="text-base h-12 px-7 text-white shadow-md hover:shadow-lg transition-shadow"
                style={{ background: brand }}
                data-testid="portal-landing-signup-cta"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Start working with us
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate(`/c/${slug}/login`)}
              className="text-base h-12 px-7"
              data-testid="portal-landing-existing-client"
            >
              <Lock className="w-4 h-4 mr-2" />
              I'm a current client
            </Button>
          </div>

          {/* Contact info — shown when signup is disabled OR as supplementary trust signal */}
          {(data.contact_email || data.contact_phone) && (
            <div className="mt-10 pt-8 border-t flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-zinc-600">
              {!data.public_signup_enabled && (
                <span className="font-medium text-zinc-700">Get in touch:</span>
              )}
              {data.contact_email && (
                <a href={`mailto:${data.contact_email}`} className="inline-flex items-center gap-1.5 hover:text-zinc-900">
                  <Mail className="w-4 h-4" /> {data.contact_email}
                </a>
              )}
              {data.contact_phone && (
                <a href={`tel:${data.contact_phone}`} className="inline-flex items-center gap-1.5 hover:text-zinc-900">
                  <Phone className="w-4 h-4" /> {data.contact_phone}
                </a>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between text-xs text-zinc-500">
          <span>© {new Date().getFullYear()} {data.name}</span>
          <span>
            Powered by{' '}
            <a href="https://countbucks.com" className="font-semibold text-zinc-700 hover:underline">CountBucks</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
